import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Layout } from '../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { Label } from '../../components/ui/label';
import { Progress } from '../../components/ui/progress';
import {
  ArrowLeft, ArrowRight, Sparkles, ShieldAlert, Ban,
  CheckCircle, AlertTriangle, Loader2, Save, Brain,
} from 'lucide-react';
import { aiActApi } from '../../lib/api';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';

const STEPS = [
  {
    id: 1, key: 'prohibited', title: 'Etapa 1 — Práticas Proibidas (Art. 5)',
    description: 'Verifique se o sistema realiza alguma das práticas absolutamente proibidas pela UE.',
    questions: [
      { key: 'social_scoring', label: 'Realiza scoring social de pessoas singulares (avaliação ou classificação com base em comportamento)?' },
      { key: 'subliminal_techniques', label: 'Utiliza técnicas subliminares fora da consciência da pessoa para distorcer o seu comportamento?' },
      { key: 'exploits_vulnerabilities', label: 'Explora vulnerabilidades de pessoas por idade, deficiência ou situação económica?' },
      { key: 'realtime_biometric_id', label: 'Faz identificação biométrica remota em tempo real em espaços públicos para fins de aplicação da lei?' },
      { key: 'biometric_categorization', label: 'Faz categorização biométrica baseada em raça, opiniões políticas, religião, orientação sexual?' },
      { key: 'crime_prediction', label: 'Faz predição individual de risco de crime baseada apenas em perfil ou características pessoais?' },
    ],
  },
  {
    id: 2, key: 'high', title: 'Etapa 2 — Alto Risco (Anexo III)',
    description: 'Verifique se o sistema se enquadra numa das áreas de alto risco.',
    questions: [
      { key: 'high_risk_critical_infra', label: 'É componente de segurança em infraestruturas críticas (água, gás, eletricidade, tráfego)?' },
      { key: 'high_risk_education', label: 'É utilizado em educação ou formação profissional (admissão, avaliação, deteção de fraude)?' },
      { key: 'high_risk_employment', label: 'É utilizado em emprego, gestão de trabalhadores ou acesso ao auto-emprego (recrutamento, promoções, monitorização)?' },
      { key: 'high_risk_essential_services', label: 'É utilizado para acesso a serviços essenciais privados ou públicos (crédito, segurança social, emergência)?' },
      { key: 'high_risk_law_enforcement', label: 'É utilizado por autoridades de aplicação da lei (avaliação de risco, polígrafo, perfilação)?' },
      { key: 'high_risk_migration', label: 'É utilizado em migração, asilo ou controlo de fronteiras?' },
      { key: 'high_risk_justice_democracy', label: 'É utilizado em administração da justiça ou processos democráticos (eleições)?' },
    ],
  },
  {
    id: 3, key: 'gpai', title: 'Etapa 3 — Modelos GPAI (Arts. 51-56)',
    description: 'O sistema é, ou utiliza, um modelo de IA de propósito geral?',
    questions: [
      { key: 'is_gpai', label: 'É um modelo de IA de propósito geral (GPAI), capaz de executar competentemente uma vasta gama de tarefas distintas (ex: LLM, modelo de visão multimodal)?' },
    ],
  },
  {
    id: 4, key: 'limited', title: 'Etapa 4 — Risco Limitado (Art. 52)',
    description: 'Verifique se o sistema requer obrigações de transparência.',
    questions: [
      { key: 'is_chatbot', label: 'Interage diretamente com pessoas singulares (chatbot, assistente virtual)?' },
      { key: 'generates_synthetic_content', label: 'Gera conteúdo sintético (texto, imagem, áudio, vídeo) ou deepfakes?' },
      { key: 'emotion_recognition', label: 'Faz reconhecimento de emoções ou categorização biométrica não-sensível?' },
    ],
  },
];

const defaultAnswers = STEPS.reduce((acc, step) => {
  step.questions.forEach(q => { acc[q.key] = false; });
  return acc;
}, {});

const RESULT_CFG = {
  prohibited: { label: 'PROIBIDO', icon: Ban, color: 'text-red-700', bg: 'bg-red-50 border-red-300' },
  high: { label: 'ALTO RISCO', icon: ShieldAlert, color: 'text-orange-700', bg: 'bg-orange-50 border-orange-300' },
  limited: { label: 'RISCO LIMITADO', icon: AlertTriangle, color: 'text-amber-700', bg: 'bg-amber-50 border-amber-300' },
  minimal: { label: 'RISCO MÍNIMO', icon: CheckCircle, color: 'text-green-700', bg: 'bg-green-50 border-green-300' },
};

export default function AiClassifyWizard() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [system, setSystem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stepIdx, setStepIdx] = useState(0);
  const [answers, setAnswers] = useState(defaultAnswers);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    aiActApi.getSystem(id)
      .then(res => {
        setSystem(res.data);
        if (res.data.classification_answers) {
          setAnswers({ ...defaultAnswers, ...res.data.classification_answers });
        }
      })
      .catch(() => toast.error('Erro ao carregar sistema'))
      .finally(() => setLoading(false));
  }, [id]);

  const isLast = stepIdx === STEPS.length - 1;
  const isFirst = stepIdx === 0;
  const step = STEPS[stepIdx];

  const handleNext = async () => {
    if (isLast) {
      // Run preview classification
      setPreviewing(true);
      try {
        const res = await aiActApi.classifyPreview(answers);
        setPreview(res.data);
      } catch { toast.error('Erro ao classificar'); }
      finally { setPreviewing(false); }
    } else {
      setStepIdx(stepIdx + 1);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await aiActApi.classifySystem(id, answers);
      toast.success(`Classificado como ${res.data.classification.risk_level}`);
      navigate(`/ai-act/systems/${id}`);
    } catch { toast.error('Erro ao guardar classificação'); }
    finally { setSaving(false); }
  };

  if (loading) return <Layout title="Classificação"><div className="flex justify-center h-64 items-center"><Loader2 className="h-8 w-8 animate-spin" /></div></Layout>;
  if (!system) return <Layout title="Classificação"><div className="text-center py-12">Sistema não encontrado</div></Layout>;

  // Preview screen
  if (preview) {
    const cfg = RESULT_CFG[preview.risk_level] || RESULT_CFG.minimal;
    const Icon = cfg.icon;
    return (
      <Layout title={`Classificação — ${system.name}`}>
        <div className="space-y-6 max-w-3xl mx-auto" data-testid="classify-result">
          <Link to={`/ai-act/systems/${id}`} className="inline-flex items-center text-sm text-slate-600 hover:text-slate-900">
            <ArrowLeft className="h-4 w-4 mr-1" />Cancelar
          </Link>

          <Card className={cn('card-flat border-2', cfg.bg)}>
            <CardContent className="p-6 text-center">
              <Icon className={cn('h-16 w-16 mx-auto mb-3', cfg.color)} />
              <h2 className={cn('text-3xl font-bold mb-2', cfg.color)} data-testid="result-risk-label">{cfg.label}</h2>
              <p className="text-sm text-slate-700 italic">{preview.risk_basis}</p>
            </CardContent>
          </Card>

          <Card className="card-flat">
            <CardHeader><CardTitle>Obrigações Aplicáveis</CardTitle></CardHeader>
            <CardContent>
              <ul className="space-y-2" data-testid="result-obligations">
                {preview.applicable_obligations.map((ob, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-indigo-600 mt-0.5 shrink-0" />
                    <span className="text-sm text-slate-700">{ob}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-4 p-3 bg-slate-50 rounded-lg">
                <p className="text-sm">
                  <strong>Avaliação recomendada:</strong>{' '}
                  <Badge variant="outline" className="ml-1" data-testid="recommended-assessment">{preview.recommended_assessment}</Badge>
                </p>
              </div>
            </CardContent>
          </Card>

          <div className="flex justify-between gap-2">
            <Button variant="outline" onClick={() => setPreview(null)} data-testid="back-to-wizard">
              <ArrowLeft className="h-4 w-4 mr-1" />Voltar e rever
            </Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-classification-btn">
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              Aplicar Classificação
            </Button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title={`Classificação — ${system.name}`}>
      <div className="space-y-6 max-w-3xl mx-auto" data-testid="classify-wizard">

        <Link to={`/ai-act/systems/${id}`} className="inline-flex items-center text-sm text-slate-600 hover:text-slate-900">
          <ArrowLeft className="h-4 w-4 mr-1" />Voltar
        </Link>

        <Card className="card-flat bg-indigo-50 border-indigo-200">
          <CardContent className="p-4 flex items-center gap-3">
            <Sparkles className="h-5 w-5 text-indigo-600" />
            <div className="flex-1">
              <p className="text-sm font-medium text-slate-900">Wizard de Classificação de Risco</p>
              <p className="text-xs text-slate-600">Baseado nos Anexos II e III do Regulamento UE 2024/1689</p>
            </div>
            <Badge variant="outline" className="text-xs" data-testid="step-indicator">Passo {step.id} de {STEPS.length}</Badge>
          </CardContent>
        </Card>

        <Progress value={((stepIdx + 1) / STEPS.length) * 100} data-testid="wizard-progress" />

        <Card className="card-flat">
          <CardHeader>
            <CardTitle>{step.title}</CardTitle>
            <p className="text-sm text-slate-600">{step.description}</p>
          </CardHeader>
          <CardContent className="space-y-4">
            {step.questions.map(q => (
              <label
                key={q.key}
                htmlFor={q.key}
                className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100 transition-colors"
              >
                <Checkbox
                  id={q.key}
                  checked={!!answers[q.key]}
                  onCheckedChange={c => setAnswers({ ...answers, [q.key]: !!c })}
                  data-testid={`q-${q.key}`}
                />
                <span className="text-sm text-slate-800">{q.label}</span>
              </label>
            ))}
          </CardContent>
        </Card>

        <div className="flex justify-between gap-2">
          <Button variant="outline" onClick={() => setStepIdx(stepIdx - 1)} disabled={isFirst} data-testid="wizard-prev-btn">
            <ArrowLeft className="h-4 w-4 mr-1" />Anterior
          </Button>
          <Button onClick={handleNext} disabled={previewing} data-testid="wizard-next-btn">
            {previewing && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {isLast ? 'Ver Classificação' : 'Próximo'}
            <ArrowRight className="h-4 w-4 ml-1" />
          </Button>
        </div>

      </div>
    </Layout>
  );
}
